import { memo } from "react";
import { Heart } from "lucide-react";
import type { GoldenSerenityTemplateProps } from "./types";
import { GS, formatDate, isRsvpCutoffPassed } from "./utils";
import { AmbientGlow, OrganicDivider } from "./decorations";

export const GoldenSerenityRSVP = memo(({ event, config, locale, t, onRsvpClick }: GoldenSerenityTemplateProps) => {
  // JUB-23: do not render at all if RSVP is disabled or the cutoff has passed.
  if (!event.rsvpSettings?.rsvpEnabled || isRsvpCutoffPassed(event)) return null;

  const dueDate = event.rsvpSettings.dueDate || event.rsvpCutoffDate;

  return (
    <section
      id="rsvp"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.creamLight}, ${GS.creamDeep})` }}
    >
      <AmbientGlow />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10 text-center space-y-8">
        <div>
          <div className="mb-8">
            <OrganicDivider width={80} />
          </div>
          <h2
            className="text-4xl sm:text-6xl font-medium mb-3"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {t("events.form.template.kindlyRespond")}
          </h2>
          {dueDate && (
            <p
              className="text-sm tracking-wide"
              style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
            >
              {formatDate(dueDate, locale, { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => {
              if (onRsvpClick) {
                onRsvpClick();
              } else {
                const element = document.getElementById("rsvp");
                if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            className="inline-flex items-center gap-3 px-10 sm:px-14 py-4 rounded-full font-semibold text-sm sm:text-base tracking-wide transition-all duration-500 hover:shadow-[0_8px_24px_rgba(201,168,106,0.3)]"
            style={{
              background: GS.gold,
              // JUB-90: creamLight-on-gold was 2.1:1 (fails WCAG AA); GS.ink gives ~5.7:1
              color: GS.ink,
              fontFamily: config.typography.body,
            }}
          >
            <Heart className="h-5 w-5" aria-hidden="true" />
            {t("events.form.template.rsvpToWedding")}
          </button>
        </div>
      </div>
    </section>
  );
});

GoldenSerenityRSVP.displayName = "GoldenSerenityRSVP";
